import React, { useCallback, useEffect, useState } from 'react'
import { SimilarNonprofitsComponentProps } from './similar-nonprofits-component.types'
import { useTranslation } from 'react-i18next'
import { useSimilarNonprofitsComponentStyles } from './similar-nonprofits-component.styles'
import { Nonprofit } from '../../../models'
import { NonProfit } from '../../../components/non-profit'
import { Typography, useMediaQuery, useTheme } from '@material-ui/core'
import { nonprofitsApi } from '../../../services/api/nonprofits'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import useEmblaCarousel from 'embla-carousel-react'
import { NonProfitCardPlaceholder } from '../../../components/non-profit/non-profit.components/non-profit-card-placeholder'

export const SimilarNonprofits: React.FC<SimilarNonprofitsComponentProps> = (
  props: SimilarNonprofitsComponentProps
) => {
  const { nonprofitId, categoryId, setSimilarNonprofits } = props

  const [categoryName, setCategoryName] = useState<string>('')
  const [nonprofitsInCategory, setNonprofitsInCategory] = useState<any>([])
  const classes = useSimilarNonprofitsComponentStyles()
  const theme = useTheme()
  const [loading, setLoading] = useState<boolean>(true)
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)
  const { t } = useTranslation()

  const similarNonprofitsSearchLink = `/nonprofit-catalog?pageIndex=1&pageSize=12&nonprofitCategoryId=${categoryId}`

  useEffect(() => {
    const requestParams: any = {
      pageSize: 12,
      pageIndex: 0,
      categoryId: categoryId,
    }

    if (categoryId) {
      nonprofitsApi
        .categories()
        .then((categories) =>
          setCategoryName(
            categories.find(
              (category: any) => category.nonprofitCategoryId === categoryId
            ).name
          )
        )

      nonprofitsApi
        .lookupData(requestParams)
        .then((data) => {
          const filtered = data.filter(
            (nonprofit: any) => nonprofit.nonprofitId !== nonprofitId
          )
          setNonprofitsInCategory(filtered)
          setSimilarNonprofits(!!filtered.length)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [categoryId])

  // Determine screen size - check from largest to smallest
  const isXl = useMediaQuery(theme.breakpoints.up('xl')) // ≥1920px
  const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl')) // 1280px-1919px
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg')) // 960px-1279px
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')) // 600px-959px
  const isXs = useMediaQuery(theme.breakpoints.down('sm')) // <600px

  // Get number of slides to scroll based on screen size
  // Should match the number of visible slides for better UX
  const getSlidesToScroll = useCallback(() => {
    if (isXl) return 1 // Extra large screens (≥1920px): 3 visible, scroll by 1
    if (isLg) return 1 // Large screens (1280px-1919px): 3 visible, scroll by 1
    if (isMd) return 1 // Medium screens (960px-1279px): 2 visible, scroll by 1
    if (isSm) return 1 // Small screens (600px-959px): 2 visible, scroll by 1
    if (isXs) return 1 // Extra small screens (<600px): 1 visible, scroll by 1
    return 1 // Default: scroll by 1
  }, [isXs, isSm, isMd, isLg, isXl])

  // Base options for embla-carousel
  const OPTIONS = {
    align: 'start' as const,
    loop: false,
    skipSnaps: false,
    dragFree: true,
    containScroll: 'trimSnaps' as const,
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS)
  const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll())

  // Update slidesToScroll when screen size changes and reinitialize carousel
  useEffect(() => {
    const newSlidesToScroll = getSlidesToScroll()
    if (newSlidesToScroll !== slidesToScroll) {
      setSlidesToScroll(newSlidesToScroll)
    }
  }, [isXs, isSm, isMd, isLg, isXl, getSlidesToScroll, slidesToScroll])

  // Reinitialize carousel when screen size changes
  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit()
    }
  }, [emblaApi, slidesToScroll])

  // Custom scroll functions that scroll by multiple slides
  const scrollPrev = useCallback(() => {
    if (!emblaApi) return

    const currentIndex = emblaApi.selectedScrollSnap()
    const newIndex = Math.max(0, currentIndex - slidesToScroll)
    emblaApi.scrollTo(newIndex)
  }, [emblaApi, slidesToScroll])

  const scrollNext = useCallback(() => {
    if (!emblaApi) return

    const currentIndex = emblaApi.selectedScrollSnap()
    const scrollSnaps = emblaApi.scrollSnapList()
    const newIndex = Math.min(
      scrollSnaps.length - 1,
      currentIndex + slidesToScroll
    )
    emblaApi.scrollTo(newIndex)
  }, [emblaApi, slidesToScroll])

  const onSelect = useCallback((emblaApi: any) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className={classes.section}>
      <div className={classes.sliderHolder}>
        <>
          {' '}
          {/* the <></> are nessesary for the arrow styling */}
          {nonprofitsInCategory?.length ? (
            <div className={classes.header}>
              <Typography variant="h6" className={[classes.bold].join(' ')}>
                {t('nonprofitCatalog.similarNonprofits')}
              </Typography>
              <a
                href={similarNonprofitsSearchLink}
                className={[
                  classes.bold,
                  classes.similarNonprofitsSearchLink,
                ].join(' ')}
              >
                {t('nonprofitCatalog.seeAll')}
              </a>
            </div>
          ) : (
            <></>
          )}
          <div className={classes.embla} ref={emblaRef}>
            <div className={classes.emblaContainer}>
              {loading &&
                [1, 2, 3].map((_, key) => (
                  <div className={classes.emblaSlide} key={key}>
                    <NonProfitCardPlaceholder></NonProfitCardPlaceholder>
                  </div>
                ))}
              {nonprofitsInCategory && nonprofitsInCategory.length > 0 ? (
                nonprofitsInCategory.map((item: Nonprofit, key: number) => (
                  <div className={classes.emblaSlide} key={key}>
                    <NonProfit {...item} nonprofitCategoryName={categoryName} />
                  </div>
                ))
              ) : (
                <></>
              )}
            </div>
          </div>
          {nonprofitsInCategory && nonprofitsInCategory.length > 0 ? (
            <>
              <button
                className={[classes.emblaButton, classes.emblaButtonPrev].join(
                  ' '
                )}
                onClick={scrollPrev}
                disabled={prevBtnDisabled}
              >
                <ChevronLeftIcon />
              </button>
              <button
                className={[classes.emblaButton, classes.emblaButtonNext].join(
                  ' '
                )}
                onClick={scrollNext}
                disabled={nextBtnDisabled}
              >
                <ChevronRightIcon />
              </button>
            </>
          ) : (
            <></>
          )}
        </>
      </div>
    </div>
  )
}
